import React from "react";
import { useTenant } from "../context/TenantContext";
import "../styles/cancellation.css";

const CancellationPolicy = () => {
  const { tenant } = useTenant();

  // Helper function to format phone number for tel: links
  const formatTelLink = (phone) => {
    if (!phone) return "";
    return phone.replace(/[\s\-\(\)]/g, "");
  };

  // Get cancellation policy content from tenant
  const cancellationContent = tenant?.CancellationContent;

  // Check if we have custom content from the admin
  const hasCustomContent =
    cancellationContent && cancellationContent.trim() !== "";

  // If custom content exists, render it directly
  if (hasCustomContent) {
    return (
      <div className="cancellation-page">
        <div className="container">
          <section className="cancellation-hero">
            <h1>Cancellation Policy</h1>
            <p>Understanding our cancellation terms and conditions</p>
          </section>
          <div
            className="cancellation-content"
            dangerouslySetInnerHTML={{ __html: cancellationContent }}
          />
        </div>
      </div>
    );
  }

  // Default content using tenant data from backend
  return (
    <div className="cancellation-page">
      <div className="container">
        <section className="cancellation-hero">
          <h1>Cancellation Policy</h1>
          <p>Understanding our cancellation terms and conditions</p>
        </section>

        <div className="cancellation-content">
          <div className="cancellation-section">
            <h2>Cancellation Policy</h2>
            <p>
              At {tenant?.BusinessName || "our company"}, we understand that
              plans can change. This policy outlines our terms and conditions
              regarding order cancellations.
            </p>
          </div>

          <div className="cancellation-section">
            <h3>Cancellation Timeframe</h3>
            <p>
              You may cancel your order within{" "}
              <strong>{tenant?.CancellationHours || "24"}</strong> hours of
              placing the order without any cancellation fees. After this
              period, cancellations may be subject to a fee.
            </p>
          </div>

          <div className="cancellation-section">
            <h3>Cancellation Fees</h3>
            <p>
              {tenant?.CancellationFee &&
              parseFloat(tenant.CancellationFee) > 0 ? (
                <>
                  A cancellation fee of{" "}
                  <strong>{tenant.CancellationFee}%</strong> of the order total
                  may apply for cancellations made after the initial{" "}
                  {tenant?.CancellationHours || "24"} hour window. This fee
                  covers processing and administrative costs.
                </>
              ) : (
                <>
                  We do not charge any cancellation fees. However, orders that
                  have already been processed or shipped may not be eligible for
                  cancellation.
                </>
              )}
            </p>
          </div>

          <div className="cancellation-section">
            <h3>How to Cancel an Order</h3>
            <p>To cancel your order, please follow these steps:</p>
            <ol>
              <li>
                Log in to your account and navigate to your order history.
              </li>
              <li>
                Find the order you wish to cancel and click the "Cancel Order"
                button.
              </li>
              <li>
                Follow the on-screen instructions to complete the cancellation.
              </li>
              <li>
                You will receive a confirmation email once your cancellation is
                processed.
              </li>
            </ol>
            <p>
              Alternatively, you can contact our customer support team directly
              for assistance with cancellations.
            </p>
          </div>

          <div className="cancellation-section">
            <h3>Refund Process</h3>
            <p>
              Once your order is cancelled, we will process your refund within{" "}
              <strong>5-10 business days</strong>. Refunds will be issued to the
              original payment method used for the purchase.
            </p>
            <p>
              Please note that it may take additional time for your bank or
              payment provider to reflect the refund in your account.
            </p>
          </div>

          <div className="cancellation-section">
            <h3>Exceptions</h3>
            <ul>
              <li>
                <strong>Custom Orders:</strong> Custom or personalized items
                cannot be cancelled once production has begun.
              </li>
              <li>
                <strong>Digital Products:</strong> Digital products and
                downloadable content are non-refundable once accessed.
              </li>
              <li>
                <strong>Service Bookings:</strong> Service bookings require{" "}
                <strong>48 hours</strong> notice for cancellation. Late
                cancellations may result in a full charge.
              </li>
              <li>
                <strong>Sale Items:</strong> Items purchased during sales or
                promotions may be subject to special cancellation terms.
              </li>
            </ul>
          </div>

          <div className="cancellation-section">
            <h3>Contact Us</h3>
            <p>
              If you have questions about this cancellation policy or need
              assistance with a cancellation, please contact us:
            </p>

            {/* Email - from backend */}
            {tenant?.BusinessEmail && (
              <p>
                <strong>Email:</strong>{" "}
                <a href={`mailto:${tenant.BusinessEmail}`}>
                  {tenant.BusinessEmail}
                </a>
              </p>
            )}

            {/* Phone - from backend with proper formatting */}
            {tenant?.BusinessPhone && (
              <p>
                <strong>Phone:</strong>{" "}
                <a href={`tel:${formatTelLink(tenant.BusinessPhone)}`}>
                  {tenant.BusinessPhone}
                </a>
              </p>
            )}

            {/* Address - from backend */}
            {(tenant?.AddressLine1 || tenant?.City) && (
              <p>
                <strong>Address:</strong>{" "}
                {[
                  tenant?.AddressLine1,
                  tenant?.AddressLine2,
                  tenant?.City,
                  tenant?.State,
                  tenant?.PostalCode,
                  tenant?.Country,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}

            {/* Fallback contact info if no tenant data exists */}
            {!tenant?.BusinessEmail &&
              !tenant?.BusinessPhone &&
              !tenant?.AddressLine1 &&
              !tenant?.City && (
                <p>
                  <strong>Email:</strong>{" "}
                  <a href="mailto:support@example.com">support@example.com</a>
                </p>
              )}
          </div>

          <div className="cancellation-section last-updated">
            <p className="last-updated-text">
              Last updated:{" "}
              {new Date().toLocaleDateString("en-ZA", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CancellationPolicy;
